import * as React from 'react';
import { FormModal } from '@repo/web-form';
import { FormField, Input } from '../../../components/ui/FormField';
import { Money } from '../../../components/ui/Money';
import { useToast } from '../../../components/ui/Toast';
import { vaultHooks, type PendingHandover } from '../network/vault.api';

interface RejectHandoverModalProps {
  handover: PendingHandover | null;
  onClose: () => void;
}

/**
 * Staff rejection of a pending handover: no money moves; every staged HELD_BY_AGENT receipt
 * returns to the agent's custody so a corrected handover can be submitted. The reason is
 * mandatory — it is stored on the record and shown to the agent.
 */
export function RejectHandoverModal({ handover, onClose }: RejectHandoverModalProps) {
  const { toast } = useToast();
  const rejectMutation = vaultHooks.useRejectHandover();
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (handover) {
      setReason('');
      setError(null);
    }
  }, [handover]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handover) return;
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError('A rejection reason of at least 3 characters is required');
      return;
    }

    return new Promise<void>((resolve) => {
      rejectMutation.mutate(
        { handoverId: handover.id, reason: trimmed },
        {
          onSuccess: () => {
            toast({
              type: 'success',
              title: 'Handover Rejected',
              description: 'The staged receipts have been returned to the agent’s custody.',
            });
            onClose();
            resolve();
          },
          onError: (err: Error) => {
            toast({ type: 'error', title: 'Error', description: err?.message ?? 'Failed to reject handover' });
            resolve();
          },
        },
      );
    });
  };

  return (
    <FormModal
      open={handover !== null}
      onClose={onClose}
      title="Reject Agent Handover"
      modal={{ isSubmitting: rejectMutation.isPending, handleSubmit }}
      submitLabel="Reject Handover"
    >
      <p className="text-sm text-text-secondary mb-4">
        Rejecting returns all staged receipts to <span className="font-medium">{handover?.agentName ?? handover?.agentId}</span>{' '}
        (expected <Money paise={handover?.expectedAmountPaise ?? 0} className="font-medium" />).
        No cash moves and the vault balance is unchanged.
      </p>

      <FormField label="Rejection Reason" required error={error ?? undefined}>
        <Input
          placeholder="Why this handover cannot be accepted (shown to the agent)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError(null);
          }}
          maxLength={1000}
        />
      </FormField>
    </FormModal>
  );
}
